import React, { useCallback, useEffect, useState } from "react";
import { createCustomerContact, deleteCustomerContact, getSchedulingErrorMessage, listCustomerContacts, updateCustomerContact } from "../../../api/schedulingApi";
import CustomerContactEditor, { contactPayload, emptyContact } from "./CustomerContactEditor";
import { Alert, btnDanger, btnPrimary, btnSecondary } from "./ui";

const relatedPerson = (row) => row.related_person || row.contact_person;

export default function CustomerContacts({ botId, customer, onBusyChange }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [value, setValue] = useState(emptyContact);
  const [removing, setRemoving] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { onBusyChange?.(saving); }, [onBusyChange, saving]);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listCustomerContacts(botId, customer.id) || []);
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not load contacts."));
    } finally {
      setLoading(false);
    }
  }, [botId, customer.id]);
  useEffect(() => { load(); }, [load]);
  const save = async (event) => {
    event.preventDefault();
    if (saving) return;
    setError("");
    if (!value.relationship.trim() || (!editing.id && (value.mode === "existing" ? !value.person : !value.name.trim()))) {
      setError("Select or enter a contact person and enter their relationship.");
      return;
    }
    setSaving(true);
    try {
      if (editing.id) await updateCustomerContact(botId, customer.id, editing.id, {
        relationship: value.relationship.trim(), is_primary: value.is_primary, is_guardian: value.is_guardian,
      });
      else await createCustomerContact(botId, customer.id, contactPayload(value));
      setEditing(null);
      await load();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not save contact."));
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await deleteCustomerContact(botId, customer.id, removing.id);
      setRemoving(null);
      await load();
    } catch (err) {
      setError(getSchedulingErrorMessage(err, "Could not remove contact."));
    } finally {
      setSaving(false);
    }
  };
  return <section className="space-y-4" aria-label="Customer contacts">
    <p className="text-sm text-slate-600">Relationships appear on both customer records. Contacts are optional, including for minors. Removing a relationship removes it from both views, without deleting either person.</p>
    <Alert>{error}</Alert>
    {loading ? <p role="status">Loading contacts...</p> : <>
      {!rows.length && !error && <p className="text-sm text-slate-500">No contacts linked.</p>}
      <ul className="space-y-3">{rows.map((row) => <li key={row.id} className="rounded-xl border border-slate-200 p-3">
        <p className="font-semibold text-slate-900">{relatedPerson(row).name || "Unnamed"}</p>
        <p className="text-sm text-slate-600">{relatedPerson(row).phone || "No phone"} · {row.display_relationship || row.relationship}</p>
        {row.direction === "incoming" ? <p className="text-xs text-slate-500">
          Your relationship to {relatedPerson(row).name || "this customer"}: {row.relationship}.
          {row.is_primary ? " You are their primary contact." : " You are not their primary contact."}
          {row.is_guardian ? " You have a guardian designation for them." : ""}
        </p> : <p className="text-xs text-slate-500">{row.is_primary ? "Primary contact" : "Not primary"}{row.is_guardian ? " · Guardian designation" : ""}</p>}
        <div className="mt-2 flex gap-2">
          <button type="button" disabled={saving} className={btnSecondary} onClick={() => { setEditing(row); setRemoving(null); setError(""); setValue({ ...emptyContact(), relationship: row.relationship, is_primary: row.is_primary, is_guardian: row.is_guardian }); }}>Edit relationship with {relatedPerson(row).name || "contact"}</button>
          <button type="button" disabled={saving} className={btnDanger} onClick={() => { setRemoving(row); setEditing(null); setError(""); }}>Remove {relatedPerson(row).name || "contact"}</button>
        </div>
      </li>)}</ul>
      {removing && <div className="space-y-3 rounded-xl border border-rose-200 p-3">
        <p>Remove the relationship with {relatedPerson(removing).name || "this person"} from both views? Both customer records will remain.</p>
        <button type="button" disabled={saving} className={btnSecondary} onClick={() => setRemoving(null)}>Keep relationship</button>{" "}
        <button type="button" disabled={saving} className={btnDanger} onClick={remove}>Confirm removal</button>
      </div>}
      {editing ? <form onSubmit={save} className="space-y-3 border-t border-slate-200 pt-4">
        <h4 className="font-semibold">{editing.id ? "Edit relationship" : "Add contact"}</h4>
        {editing.id && <p className="rounded-lg bg-cyan-50 p-3 text-sm text-cyan-900">
          Editing {editing.contact_person.name || "the contact person"}'s role for {editing.patient?.name || customer.name || "the patient"}.
          {" "}Relationship, primary contact and guardian designation describe the contact person's role for this patient, not the reverse.
        </p>}
        <CustomerContactEditor botId={botId} customerId={customer.id} value={value} onChange={setValue} editing={!!editing.id} disabled={saving} />
        <button type="button" disabled={saving} className={btnSecondary} onClick={() => setEditing(null)}>Cancel contact</button>{" "}
        <button type="submit" disabled={saving} className={btnPrimary}>{saving ? "Saving..." : "Save contact"}</button>
      </form> : !removing && <button type="button" disabled={saving} className={btnPrimary} onClick={() => { setEditing({}); setValue(emptyContact()); setError(""); }}>Add contact</button>}
      {error && <button type="button" disabled={saving} className={btnSecondary} onClick={load}>Retry loading contacts</button>}
    </>}
  </section>;
}
